'use client'

import React, { FC, ReactElement } from 'react'
import TransferIcon from '@/icons/transfer'
import Button from '@/components/button-gradient-epic'
import IncriptionActionInput from '@/components/inscription-actions/inscription-action-input'
import Loading from '@/components/circle-loading'
import event from '@/utils/event'
import Wallet from '@/utils/wallet'
import { API_URL } from '@/utils/constants'
import styles from './InscriptionActionTransfer.module.css'
import ActionDrawer from '@/components/action-drawer'
import { Inscription } from '@/utils/types/inscription'
import ErrorHandler from '@/utils/error-handler'
import InscriptionWarningMultiInscriptions from '@/components/inscription-warning/inscription-warning-multi-inscriptions'
import InscriptionWarningExcessSats from '@/components/inscription-warning/inscription-warning-excess-sats'
import QrScanner from '@/components/qr-scanner'
import useResolveSns from '@/utils/hooks/useResolveSns'
import FeeSlider from '@/components/fee-slider'
import { fetcher } from '@/utils/fetcher'
import useSwr from 'swr'

type ForceParams = { forceExcessSats?: boolean; forceMultiInscriptions?: boolean }

const InscriptionActionTransfer: FC<{
	open: boolean
	setOpen: (v: boolean) => void
	inscription: Inscription
	className?: string
	onSuccess?: () => void
	buttonColor: 'gray' | 'black'
}> = (props): ReactElement => {
	const { open, setOpen, inscription, onSuccess } = props
	const [feeRate, setFeeRate] = React.useState(10.0)

	const {
		value,
		handleChange,
		setValue,
		resolveSnsEl,
		sns,
		error: snsError,
		loading: snsLoading
	} = useResolveSns()

	const [loading, setLoading] = React.useState(false)
	const handleDrawerToggle = React.useCallback(() => setOpen(!open), [open, setOpen])
	const [forceParams, setForceParams] = React.useState<ForceParams>()
	const [openExcessSats, setOpenExcessSats] = React.useState(false)
	const [totalSats, setTotalSats] = React.useState<number>()
	const [openMultiInscriptions, setOpenMultiInscriptions] = React.useState(false)
	const [inscriptions, setInscriptions] = React.useState<Inscription[]>()

	const handleQRSuccess = (res: any) => setValue(res)
	const handleQRError = () =>
		event.emit('snackbar', { variant: 'error', title: 'Unable to read QR code!' })

	const { data } = useSwr(`${API_URL}/wallet/${Wallet.balanceAddress()}/all-utxos`, fetcher)

	const handleSubmit = React.useCallback(
		async (evt?: any, _params?: ForceParams) => {
			evt?.preventDefault()
			evt?.stopPropagation()

			const to_address = sns?.address || value

			console.log('here', loading, to_address)

			if (loading || to_address?.length === 0) {
				return
			}

			setLoading(true)

			try {
				let psbt

				try {
					psbt = await Wallet.getTransfers(
						[inscription.id],
						to_address,
						feeRate,
						data.utxos.map((e: any) => [e.txid, e.vout, e.value])
					)
				} catch (e) {
					console.log(e)
					const errorDetail = ErrorHandler.errorDetail(e)
					if (errorDetail?.type == 'EXCESS_SATS_IN_INSCRIPTION_OUTPOINT') {
						setTotalSats(errorDetail?.totalSats)
						setOpenExcessSats(true)
					} else if (errorDetail?.type == 'MULTIPLE_INSCRIPTIONS_IN_OUTPOINT') {
						setInscriptions(errorDetail?.inscriptions)
						setOpenMultiInscriptions(true)
					} else {
						ErrorHandler.snackbar(e)
					}
					setLoading(false)
					return
				}

				const { rawtx } = await Wallet.signTransfer(psbt, 1)

				event.emit('open-broadcast-modal', {
					type: 'transfer',
					inscriptions: [inscription],
					address: to_address,
					rawtx
				})
			} catch (e) {
				console.log(e)
				Wallet.handleError(e)
			}

			setLoading(false)
		},
		[sns?.address, value, loading, inscription, feeRate, data.utxos]
	)

	const handleConfirmExcessSats = React.useCallback(() => {
		let tmpParams = { ...forceParams, forceExcessSats: true }
		setForceParams(tmpParams)
		setOpenExcessSats(false)
		handleSubmit(null, tmpParams)
	}, [handleSubmit, forceParams])

	React.useEffect(() => {
		if (!open && !loading) {
			requestAnimationFrame(() => {
				setValue('')
				setTotalSats(0)
				setInscriptions([])
				setLoading(false)
				setForceParams(null as any)
			})
		}
	}, [open, loading, setValue])

	React.useEffect(() => {
		const handleSuccess = (payload: any) => {
			if (
				payload.event === 'success' &&
				payload.inscription &&
				payload?.inscription?.id === inscription?.id
			) {
				setOpen(false)
				onSuccess && onSuccess()
			}
		}

		event.listen('open-broadcast-modal', handleSuccess)

		return () => {
			event.removeListener('open-broadcast-modal', handleSuccess)
		}
	}, [inscription?.id, onSuccess, setOpen])

	return (
		<>
			<ActionDrawer
				title="Transfer"
				icon={() => <TransferIcon className={styles.icon} />}
				open={open}
				handleDrawerToggle={handleDrawerToggle}
				className={props.className}
				buttonColor={props?.buttonColor}
			>
				<IncriptionActionInput
					placeholder="Send to bitcoin address"
					value={value}
					onChange={handleChange}
					endAdornment={<QrScanner onSuccess={handleQRSuccess} onError={handleQRError} />}
				/>
				{resolveSnsEl}
				<div className={styles.feeSlider}>
					<FeeSlider feeRate={feeRate} setFeeRate={setFeeRate} />
				</div>
				<Button
					size="small"
					onClick={handleSubmit}
					className={styles.button}
					disabled={snsLoading || snsError}
				>
					{loading ? <Loading className={styles.loading} /> : 'Send'}
				</Button>
			</ActionDrawer>
			{openMultiInscriptions && (
				<InscriptionWarningMultiInscriptions
					inscriptions={inscriptions as any}
					open={openMultiInscriptions}
					onClose={() => setOpenMultiInscriptions(false)}
				/>
			)}
			{openExcessSats && (
				<InscriptionWarningExcessSats
					action="transfer"
					totalSats={totalSats as any}
					open={openExcessSats}
					onClose={() => setOpenExcessSats(false)}
					onConfirm={handleConfirmExcessSats}
				/>
			)}
		</>
	)
}

export default InscriptionActionTransfer
